import React, { useEffect } from 'react'

import Image from 'next/image'

import { useModuleContext } from '@/contexts/ModuleContext'
import { ForbesLogoWhite, NewForbesLogoWhite, PlusWhite } from '@/icons'
import consumerLogoWhite from '@/img/new-ethos-logo-white.svg'
import { Button } from '@getethos/ethos-design-system-v2'
import classNames from 'classnames'
import { CloudinaryImage } from 'ethos-design-system'

import { LoginExperimentButton } from '@/components/Navigation/LoginCTAExperiment'
import useGetUserStatus from '@/components/Navigation/hooks/useGetUserStatus'
import { SingleCTAInterface } from '@/components/SingleCTA/interface'

import useCheckCobrandingNewPartnersLogos from '@/hooks/cobranded/useCheckCobrandingNewPartnersLogos'
import { useForbesHeaderNavbar } from '@/hooks/cobranded/useForbesHeaderNavbar'
import { useNewForbesCobranding } from '@/hooks/cobranded/useNewForbesCobranding'
import { useIsIulPage } from '@/hooks/content/useIsIulPage'
import {
  useIulPageWordingIulExperiment,
  useIulPageWordingPermanentExperiment,
} from '@/hooks/features/useIulPageWordingExperiment'
import { useLoginCTAExperiment } from '@/hooks/features/useLoginCTAExperiment'
import { usePricingStats } from '@/hooks/features/usePricingStats'
import { useWreathsFooterUpdate } from '@/hooks/features/useWreathsFooterUpdate'
import useCTABrandNewColors from '@/hooks/useCTABrandNewColors'
import useDetectScreenSize from '@/hooks/useDetectScreenSize'

import styles from './SingleCTADirect.module.scss'
import {
  TRUST_IMAGE_GROUPS_FFFFFF,
  TRUST_IMAGE_WREATH_IUL_PAGE,
  TRUST_IMAGE_WREATH_UPDATE_WHITE,
} from './constants'

interface Props {
  ctaClick: (props: { flow?: string; label?: string }) => void
  moduleData: SingleCTAInterface
}

const SingleCTADirect = ({ ctaClick, moduleData }: Props) => {
  const { isSpecialSingleCTAPage, useOldFonts = false } = moduleData
  const { isLaptopAndUp, isTabletAndUp, isMobile } = useDetectScreenSize()

  let partnerLogoMobile = useCheckCobrandingNewPartnersLogos()
  const isForbesEnabled = useForbesHeaderNavbar()
  const isNewForbesEnabled = useNewForbesCobranding()
  const isIulPage = useIsIulPage()

  const {
    navBarControls: { hideNavBar },
  } = useModuleContext()

  const { isTreatment } = useWreathsFooterUpdate()

  useEffect(() => {
    hideNavBar(!isLaptopAndUp)
  }, [hideNavBar, isLaptopAndUp])

  const ctaLabel = moduleData.ctaLabel || 'Check my price'

  const {
    variation: loginCTAExperimentVariation,
    isTreatment: isLoginCTAExperimentTreatment,
  } = useLoginCTAExperiment()

  const {
    isTreatment: isPricingStatsTreatment,
    isTreatment1: isPricingStatsTreatment1,
    isTreatment2: isPricingStatsTreatment2,
  } = usePricingStats()

  const { isIulWordingExperimentEnabled } = useIulPageWordingIulExperiment()
  const useIulWording =
    isIulWordingExperimentEnabled ||
    moduleData.experimentKey === 'iulWordingEnabled'

  const { isPermanentWordingExperimentEnabled } =
    useIulPageWordingPermanentExperiment()

  const userDataStatus = useGetUserStatus()

  const hasNewColors = useCTABrandNewColors()

  const consumerLogo = consumerLogoWhite

  let trustImageGroups = TRUST_IMAGE_GROUPS_FFFFFF

  if (isNewForbesEnabled) {
    partnerLogoMobile = <NewForbesLogoWhite />
  } else if (isForbesEnabled) {
    partnerLogoMobile = <ForbesLogoWhite />
  }

  if (isTreatment) {
    trustImageGroups = TRUST_IMAGE_WREATH_UPDATE_WHITE
  }

  if (isSpecialSingleCTAPage) {
    trustImageGroups = TRUST_IMAGE_WREATH_UPDATE_WHITE
  }

  if (isIulPage) {
    trustImageGroups = TRUST_IMAGE_WREATH_IUL_PAGE
  }

  let bgImage: React.ReactNode | null = null

  bgImage = (
    <div
      className={classNames(
        styles.bgImage,
        styles.huggingImage,
        styles.showBgImageMobile
      )}
    >
      <Image
        priority
        src="https://res.cloudinary.com/getethos/image/upload/v1743635480/family_hugging_mu2wca.png"
        alt="Father mother and child hugging"
        width={500}
        height={560}
        quality={100}
      />
    </div>
  )
  let headline = (
    <>
      Instant <br />
      life insurance
    </>
  )

  let subtitle = ['no medical exams', 'no blood tests']

  if (isPricingStatsTreatment1) {
    headline = (
      <>
        Save over 50%
        <br />
        on life insurance
      </>
    )
  }

  if (isPricingStatsTreatment2) {
    headline = (
      <>
        Save over 45%
        <br />
        on life insurance
      </>
    )
  }

  if (isIulPage && useIulWording) {
    headline = (
      <>
        Indexed Universal
        <br /> Life, instantly
      </>
    )
    subtitle = [
      'Help grow your financial wealth while protecting your loved ones.',
    ]
  }

  if (isIulPage && isPermanentWordingExperimentEnabled) {
    headline = <>Permanent life insurance{isTabletAndUp && ','} instantly</>
    subtitle = [
      'Help grow your financial wealth while protecting your loved ones.',
    ]
  }

  return (
    <section
      className={classNames(styles.wrapper, styles.trustworthy, {
        [styles.specialSingleCTAPage]: isSpecialSingleCTAPage,
        [styles.isWordingExperiment]:
          isIulPage && (useIulWording || isPermanentWordingExperimentEnabled),
      })}
    >
      <div
        className={classNames(
          styles.container,
          styles.trustworthy,
          styles.showBgImageMobile,
          {
            [styles.newColors]: hasNewColors,
            [styles.newFonts]: !useOldFonts,
          }
        )}
      >
        <div
          className={classNames(styles.mainContent, {
            [styles.newFonts]: !useOldFonts,
            [styles.pricingStatsTreatment]: isPricingStatsTreatment,
          })}
        >
          <div
            className={classNames(
              partnerLogoMobile ? styles.logoWithPartner : styles.logo,
              {
                'flex w-full flex-row items-center justify-between':
                  isLoginCTAExperimentTreatment && !isSpecialSingleCTAPage,
              }
            )}
          >
            {partnerLogoMobile ? (
              <div className="mr-auto flex w-full flex-row items-center gap-4">
                {!isNewForbesEnabled && (
                  <>
                    <Image
                      src={consumerLogo.src}
                      alt="Ethos Logo"
                      width={87}
                      height={16}
                    />
                    {<PlusWhite />}
                  </>
                )}
                <div
                  className={classNames(styles.mobileLogo, {
                    [styles.forbesLogo]: isNewForbesEnabled,
                  })}
                >
                  {partnerLogoMobile}
                </div>
              </div>
            ) : (
              <img src={consumerLogo.src} alt="Ethos Logo" />
            )}

            {!isSpecialSingleCTAPage && isLoginCTAExperimentTreatment && (
              <LoginExperimentButton
                isMobile
                treatment={loginCTAExperimentVariation}
                userDataStatus={userDataStatus}
              />
            )}
          </div>
          {isIulPage && (
            <div className={styles.trustpilotEyebrow}>
              <CloudinaryImage
                className={styles.trustpilotLogoMobile}
                publicId="https://res.cloudinary.com/getethos/image/upload/v1753317161/Brand_logos_mrsrnb.svg"
                alt="Trustpilot Logo Mobile"
                crop={CloudinaryImage.CROP_METHODS.FIT}
                height={[22, 22, 22, 22]}
                width={[22, 22, 22, 22]}
              />
              <CloudinaryImage
                className={styles.trustpilotLogoDesktop}
                publicId="https://res.cloudinary.com/getethos/image/upload/v1753317161/Group_wj3bop.svg"
                alt="Trustpilot Logo Desktop"
                crop={CloudinaryImage.CROP_METHODS.FIT}
                height={[18, 18, 18, 18]}
                width={[98, 98, 98, 98]}
              />
              <p>
                <strong>4.8 stars</strong> with <strong>2600+</strong> reviews
                on Trustpilot
              </p>
            </div>
          )}
          <h1 className={classNames(styles.header, styles.trustworthy)}>
            {headline}
          </h1>
          <div
            className={classNames(
              'flex items-center gap-2',
              isLaptopAndUp ? 'mb-10' : 'mb-4',
              isSpecialSingleCTAPage && 'mb-[32px]'
            )}
          >
            {subtitle.map((sub, index) => (
              <React.Fragment key={`subtitle-${index}`}>
                <p className={classNames(styles.subheader, styles.trustworthy)}>
                  {sub}
                </p>
                {index < subtitle.length - 1 && (
                  <div className="mb-1 h-[6px] w-[6px] rounded-full bg-[#82B0AB]"></div>
                )}
              </React.Fragment>
            ))}
          </div>

          <Button
            className={classNames(styles.button, {
              [styles.newColors]: hasNewColors,
              [styles.newFonts]: !useOldFonts,
            })}
            buttonTitle={ctaLabel}
            alignment="center"
            variant="salamander"
            type="submit"
            size="lg"
            onClick={() => ctaClick({ label: ctaLabel })}
          />
          <div className={classNames(styles.trustImages)}>
            {trustImageGroups.map((trustImageGroup, index) => (
              <div className={styles.trustImage} key={`trustImage-${index}`}>
                <CloudinaryImage
                  publicId={trustImageGroup.groupImage}
                  alt={trustImageGroup.imageAlt}
                  className={styles.trustImage}
                  crop={CloudinaryImage.CROP_METHODS.FIT}
                  height={[101, 101, 101, 101]}
                  width={[170, 170, 170, 170]}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className={classNames(styles.bgContainer, styles.showBgImageMobile)}
        >
          {bgImage}
        </div>
      </div>
    </section>
  )
}

export default SingleCTADirect
